import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenantProtection } from '../../../hooks/useTenantProtection';
import toast from 'react-hot-toast';
import { X, Download, Save, FileSpreadsheet, FileText, Code } from 'lucide-react';

const ExportModal = ({ reportId, reportName, parameters = {}, onClose }) => {
  const { user } = useAuth();
  const { getProtectedTenantCode } = useTenantProtection();

  const [selectedFormat, setSelectedFormat] = useState('excel');
  const [saveAsAttachment, setSaveAsAttachment] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [includeHeader, setIncludeHeader] = useState(true);
  const [includeFooter, setIncludeFooter] = useState(true);
  const [sendToEmail, setSendToEmail] = useState(false);

  const formats = [
    {
      id: 'excel',
      name: 'Excel',
      description: 'Export as XLSX file with data tables',
      icon: FileSpreadsheet,
      color: 'green'
    },
    {
      id: 'pdf',
      name: 'PDF',
      description: 'Export as PDF document',
      icon: FileText,
      color: 'red'
    },
    {
      id: 'html',
      name: 'HTML',
      description: 'Export as HTML file with styling',
      icon: Code,
      color: 'blue'
    }
  ];

  const handleExport = async () => {
    try {
      setExporting(true);
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/reports/${reportId}/export`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          format: selectedFormat,
          saveAsAttachment,
          parameters,
          includeHeader,
          includeFooter,
          sendToEmail
        })
      });

      if (!response.ok) {
        throw new Error('Failed to export report');
      }

      if (sendToEmail || saveAsAttachment) {
        const data = await response.json();
        if (sendToEmail && saveAsAttachment) {
          toast.success('Report emailed and saved as attachment');
        } else if (sendToEmail) {
          toast.success('Report emailed successfully');
        } else {
          toast.success('Report saved as attachment successfully');
        }
        onClose();
      } else {
        // Download file
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        
        const contentDisposition = response.headers.get('content-disposition');
        const filename = contentDisposition
          ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
          : `${reportName.replace(/[^a-z0-9]/gi, '_')}.${selectedFormat === 'excel' ? 'xlsx' : selectedFormat}`;
        
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        toast.success(`Report exported as ${selectedFormat.toUpperCase()}`);
        onClose();
      }
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Export Report
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Choose a format to export "{reportName}"
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Format Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Export Format
            </label>
            <div className="grid grid-cols-1 gap-3">
              {formats.map((format) => {
                const Icon = format.icon;
                const isSelected = selectedFormat === format.id;
                
                return (
                  <button
                    key={format.id}
                    onClick={() => setSelectedFormat(format.id)}
                    className={`flex items-start gap-4 p-4 rounded-lg border-2 transition-all text-left ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className={`p-2 rounded-lg ${
                      format.color === 'green' ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' :
                      format.color === 'red' ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' :
                      'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                    }`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                        {format.name}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {format.description}
                      </p>
                    </div>
                    {isSelected && (
                      <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Options */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Export Options
            </label>
            <label className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750">
              <input
                type="checkbox"
                checked={saveAsAttachment}
                onChange={(e) => setSaveAsAttachment(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Save className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    Save as Attachment
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Save the exported report to the attachments library instead of downloading
                </p>
              </div>
            </label>
            <div className="mt-3 space-y-3">
              <label className={`flex items-center gap-3 p-4 rounded-lg border border-gray-200 dark:border-gray-700 ${!user?.email ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750'}`}>
                <input
                  type="checkbox"
                  checked={sendToEmail}
                  onChange={(e) => setSendToEmail(e.target.checked)}
                  disabled={!user?.email}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    Email to Me
                  </span>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Send the exported report to {user?.email || 'your email'}
                  </p>
                </div>
              </label>
              <label className={`flex items-center gap-3 p-4 rounded-lg border border-gray-200 dark:border-gray-700 ${selectedFormat === 'excel' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750'}`}>
                <input
                  type="checkbox"
                  checked={includeHeader}
                  onChange={(e) => setIncludeHeader(e.target.checked)}
                  disabled={selectedFormat === 'excel'}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    Include Header
                  </span>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Show logo, company name, and report date in the header
                  </p>
                </div>
              </label>
              <label className={`flex items-center gap-3 p-4 rounded-lg border border-gray-200 dark:border-gray-700 ${selectedFormat === 'excel' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750'}`}>
                <input
                  type="checkbox"
                  checked={includeFooter}
                  onChange={(e) => setIncludeFooter(e.target.checked)}
                  disabled={selectedFormat === 'excel'}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    Include Footer
                  </span>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Show logo, company name, and report date in the footer
                  </p>
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            disabled={exporting}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="btn btn-primary flex items-center gap-2"
          >
            {exporting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Exporting...
              </>
            ) : (
              <>
                {saveAsAttachment ? <Save className="w-4 h-4" /> : <Download className="w-4 h-4" />}
                {saveAsAttachment ? 'Save' : 'Download'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;

